'use client';

import React, { useState, useCallback, useMemo } from 'react';
import { Input, Chip, Avatar, Tooltip, Button, Snippet } from '@heroui/react';
import { Icon } from '@iconify/react';
import { IdView, Typography, UniversalTable } from '../../../../../components';
import { FilterSelect, ResponsiveTooltip } from '../../../../../components/ui';
import {
  EyeIc,
  GoogleIc,
  InstagramIc,
  LinkIcon,
  TwitterIc,
  UserNoAvatarIcon,
  ChatRoundedIc,
  GroupUserIc,
  MoneyIc,
  UserCheckIc,
  IcReddit,
  IcTikTok,
  IcTg,
  IcYoutube,
  ResetFiltersIc,
  EmptyContentClientIc,
  OnlyFansIc,
  GlobalIc,
  IcAnotherSoc,
} from '../../../../../components/icons';
import clientRevshareService, {
  RevshareOrder,
} from '../../../../../api/client/clientRevshareService';
import searchIconUrl from './search-icon.svg';
import { InfoCircle } from '../../../../../components/icons';
import { IcDate } from '../../../../../components/icons';
import CampaignStatsTooltip from './CampaignStatsTooltip';
import { useTranslations } from '../../../../../contexts/translation.context';

// Map source to icon component
const SOURCE_ICONS: Record<string, React.ReactNode> = {
  all: <GlobalIc size={14} />,
  only_fans: <OnlyFansIc size={14} fill="#A8A8B0" />,
  onlyfans: <OnlyFansIc size={14} fill="#A8A8B0" />,
  instagram: <InstagramIc size={14} fill="#A8A8B0" />,
  twitter: <TwitterIc size={14} fill="#A8A8B0" />,
  google: <GoogleIc size={14} fill="#A8A8B0" />,
  reddit: <IcReddit size={14} />,
  tiktok: <IcTikTok size={14} />,
  telegram: <IcTg size={14} />,
  youtube: <IcYoutube size={14} />,
  another: <IcAnotherSoc size={14} />,
  other: <IcAnotherSoc size={14} />,
};

// Map status to display info
const STATUS_CONFIG: Record<string, { color: string; bgColor: string }> = {
  in_progress: { color: '#17c964', bgColor: '#f4f4f5' },
  in_moderation: { color: '#f5a524', bgColor: '#f4f4f5' },
  waiting: { color: '#006fee', bgColor: '#f4f4f5' },
  canceled: { color: '#f31260', bgColor: '#f4f4f5' },
  completed: { color: '#71717a', bgColor: '#f4f4f5' },
};

interface CampaignsTabProps {
  modelId?: string;
  accountData?: any;
  onCountChange?: (count: number) => void;
}

const CampaignsTab: React.FC<CampaignsTabProps> = ({ modelId, accountData, onCountChange }) => {
  const { t } = useTranslations();
  // Safe translation with fallback if key missing
  const tt = React.useCallback(
    (key: string, fallback: string) => {
      const val = t(key);
      return val && val !== key ? val : fallback;
    },
    [t]
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [campaigns, setCampaigns] = useState<RevshareOrder[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Check if there are any active filters
  const hasActiveFilters = useMemo(() => {
    return searchQuery !== '' || statusFilter !== '';
  }, [searchQuery, statusFilter]);

  // Reset filters handler
  const handleResetFilters = useCallback(() => {
    setSearchQuery('');
    setStatusFilter('');
    setCurrentPage(1);
  }, []);

  // Загрузка данных о кампаниях
  const loadCampaigns = useCallback(async () => {
    setIsLoading(true);
    try {
      // modelId - это internal_id модели (MongoDB _id)
      const modelsFilter = modelId ? [modelId] : undefined;

      const response = await clientRevshareService.getOrders({
        page: currentPage,
        page_size: pageSize,
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        // Фильтр по модели если указан modelId (internal_id)
        models: modelsFilter,
      });

      setCampaigns(response.objects || []);
      setTotalCount(response.count || 0);
      onCountChange?.(response.count || 0);
    } catch (error: any) {
      console.error('Ошибка при загрузке кампаний:', error);
      setCampaigns([]);
      setTotalCount(0);
      onCountChange?.(0);
    } finally {
      setIsLoading(false);
    }
  }, [modelId, currentPage, pageSize, searchQuery, statusFilter]);

  // Загружаем данные при изменении параметров
  React.useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  // Обработчики
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleRowClick = (campaign: RevshareOrder) => {
    console.log('Клик по кампании:', campaign);
    // TODO: Добавить логику для открытия детальной информации о кампании
  };

  // Форматирование даты в относительный формат ("6д. 16ч. назад")
  const formatRelativeDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 60) {
      return `${diffMinutes}м. назад`;
    } else if (diffHours < 24) {
      return `${diffHours}ч. назад`;
    } else if (diffDays < 30) {
      const hours = diffHours % 24;
      return hours > 0 ? `${diffDays}д. ${hours}ч. назад` : `${diffDays}д. назад`;
    } else {
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }
  };

  // Форматирование даты для чипа (DD.MM.YYYY)
  const formatChipDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Получение иконки источника
  const getSourceIcon = (source: string) => {
    return SOURCE_ICONS[source?.toLowerCase()] || null;
  };

  // Получение конфига статуса
  const getStatusConfig = (status: string) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.in_progress;
  };

  // Форматирование статуса для отображения
  const formatStatus = (status: string) => {
    const statusLabels: Record<string, string> = {
      in_progress: tt('accountPage.campaigns.status.in_progress', 'Активна'),
      in_moderation: tt('accountPage.campaigns.status.in_moderation', 'На модерации'),
      waiting: tt('accountPage.campaigns.status.waiting', 'Ожидает'),
      canceled: tt('accountPage.campaigns.status.canceled', 'Отменена'),
      completed: tt('accountPage.campaigns.status.completed', 'Завершена'),
    };
    return statusLabels[status] || status;
  };

  // Определение колонок таблицы
  const colCampaign = tt('accountPage.campaigns.columns.campaign', 'Кампания');
  const colStatistics = tt('accountPage.campaigns.columns.statistics', 'Статистика');
  const colStatus = tt('accountPage.campaigns.columns.status', 'Статус');
  const colAdded = tt('accountPage.campaigns.columns.added', 'Добавлено');
  const columns = [
    { key: 'id', name: '#', label: '#' },
    { key: 'campaign', name: colCampaign, label: colCampaign },
    { key: 'statistics', name: colStatistics, label: colStatistics },
    { key: 'status', name: colStatus, label: colStatus },
    { key: 'added', name: colAdded, label: colAdded },
  ];

  // Рендер ячеек таблицы
  const renderCell = (campaign: RevshareOrder, columnKey: string) => {
    switch (columnKey) {
      case 'id':
        return (
          // <div className="text-sm text-auth-ink">
          //   {campaign.model?.track_link_id || (campaign._id ? campaign._id.slice(-6) : '-')}
          // </div>
          <IdView textToCopy={campaign._id}>{campaign._id ? campaign._id.slice(-4) : '-'}</IdView>
        );

      case 'campaign':
        const campaignData = campaign as any;
        const partnerName = campaignData.offer?.client?.name || campaign.model?.name || '-';
        const partnerAvatar = campaignData.offer?.client?.avatar || campaign.model?.avatar;
        const sourceValue = campaign.source || 'unknown';
        const commissionValue = campaign.offer?.commission ?? 0;
        return (
          <div className="flex items-start gap-4">
            <Avatar
              src={partnerAvatar}
              className="w-8 h-8 rounded-lg"
              fallback={partnerName?.charAt(0) || 'P'}
            />
            <div className="flex flex-col gap-1">
              {/* Имя партнёра */}
              <div className="flex items-center gap-1">
                <UserNoAvatarIcon size={20} />
                <ResponsiveTooltip
                  content={t('accountPage.campaigns.partnerTooltip')}
                  placement="top"
                >
                  <span className="text-sm font-medium text-auth-ink cursor-pointer">{partnerName}</span>
                </ResponsiveTooltip>
              </div>

              {/* ID кампании и username модели */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-auth-ink">
                  QV{campaign.model?.track_link_id || campaign._id?.slice(-4) || '0000'}
                </span>
                {/* <span className="text-sm text-partner">
                  @{campaign.model?.of_user_name || 'unknown'}
                </span> */}
                <ResponsiveTooltip
                  content={t('accountPage.campaigns.modelNicknameTooltip')}
                  placement="top"
                >
                  <a
                    target="__blank"
                    href={`https://onlyfans.com/${campaign.model?.of_user_name}`}
                    className="text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <Typography variant="body2" className="!text-[#006FEE]">
                      @{campaign.model?.of_user_name}
                    </Typography>
                  </a>
                </ResponsiveTooltip>
              </div>

              {/* Источник с датой, комиссия и тип */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Чип источника с датой */}
                <ResponsiveTooltip
                  content={t('accountPage.campaigns.sourceDateTooltip')}
                  placement="top"
                >
                  <div>
                    <Chip
                      variant="flat"
                      size="sm"
                      className="bg-accent/20 text-accent rounded-full px-2"
                    >
                      {sourceValue} {formatChipDate(campaign.created_at)}
                    </Chip>
                  </div>
                </ResponsiveTooltip>

                {/* Комиссия */}
                <div className="flex items-center gap-0.5">
                  <span className="text-sm text-auth-ink">{commissionValue}%</span>
                  <div className="w-4 h-4">
                    <InfoCircle />
                  </div>
                </div>

                {/* Чип источника */}
                <Chip
                  variant="bordered"
                  size="sm"
                  className="border-auth-strength-none rounded-xl py-1 px-2 gap-1"
                  startContent={getSourceIcon(sourceValue)}
                >
                  <Typography variant="body3">{sourceValue}</Typography>
                </Chip>

                <ResponsiveTooltip
                  content={t('accountPage.campaigns.byLinkTooltip')}
                  placement="top"
                >
                  <span className="text-xs font-semibold text-auth-ink cursor-pointer">
                    {t('accountPage.campaigns.byLink')}
                  </span>
                </ResponsiveTooltip>
              </div>

              {/* Ссылка */}
              {/* <div className="inline-flex items-center gap-2 bg-husky rounded-5 px-2 py-1 w-fit">
                <div className="size-[1.125rem]">
                  <LinkIcon/>
                </div>
                <span className="text-sm text-partner truncate max-w-[17rem]">
                  {campaign.model?.link || '-'}
                </span>
                <div className="flex items-center gap-[4px]">
                  <EyeIc/> */}
              <div className="flex items-center gap-1">
                <Snippet
                  symbol={null}
                  codeString={campaign.model?.link}
                  className="bg-[#E0EDFC] rounded-full px-1 py-[.1875rem]"
                  classNames={{
                    base: 'bg-[#E0EDFC] rounded-full px-2 py-[.1875rem] flex items-center gap-1 shadow-none',
                    pre: 'bg-transparent p-0 flex items-center gap-1 max-w-[220px]',
                    copyButton: 'text-[#006FEE] min-w-5 h-5',
                    checkIcon: 'text-[#006FEE]',
                    content: 'flex items-center gap-1 max-w-[13.75rem]',
                  }}
                >
                  <a
                    href={campaign.model?.link}
                    target="__blank"
                    className="flex items-center gap-1 no-underline"
                  >
                    <LinkIcon size={18} fill="#006FEE" className="text-[#006FEE]" />
                    <Typography
                      variant="body2"
                      className="!text-[#006FEE] truncate max-w-[11.25rem]"
                    >
                      {campaign.model?.link}
                    </Typography>
                  </a>
                </Snippet>
                <div className="flex items-center gap-[4px]">
                  <EyeIc />
                  <ResponsiveTooltip
                    content={t('accountPage.campaigns.guestsTooltip')}
                    placement="top"
                  >
                    <span className="text-xs text-[#a8a8b0] cursor-pointer">{campaignData.views || 0}</span>
                  </ResponsiveTooltip>
                  {campaignData.conversion_rate && (
                    <ResponsiveTooltip
                      content={t('accountPage.campaigns.conversionTooltip')}
                      placement="top"
                    >
                      <span className="text-xs text-[#a8a8b0] cursor-pointer">({campaignData.conversion_rate})</span>
                    </ResponsiveTooltip>
                  )}
                </div>
              </div>
              {/* </div>
              </div> */}
            </div>
          </div>
        );

      case 'statistics':
        const stats = campaign as any;
        const subscribedCount = Number(stats.subscribed_count) || 0;
        const verifiedCount = Number(stats.subs?.with_gte_3_chat_messages_count) || 0;
        const messagesCount = Number(stats.messages_count) || 0;
        const totalRevenue = Number(stats.transactions_statistic?.total) || 0;
        const arpu = subscribedCount > 0 ? totalRevenue / subscribedCount : 0;
        const arpuUsd = Number(stats.arpu_usd) || arpu;

        return (
          <div className="flex flex-col gap-1">
            <ResponsiveTooltip
              content={<CampaignStatsTooltip campaign={campaign} />}
              className="p-4"
            >
              <div className="flex flex-col gap-1">
                {/* Row 1: users, verified, messages */}
                <div className="flex items-center gap-2 flex-nowrap">
                  {/* Пользователи */}
                  <div className="flex items-center gap-1">
                    <GroupUserIc />
                    <Typography variant="body3">{subscribedCount}</Typography>
                  </div>

                  {/* Верифицированные */}
                  <div className="flex items-center gap-1">
                    <UserCheckIc />
                    <Typography variant="body3">{verifiedCount}</Typography>
                  </div>

                  {/* Сообщения */}
                  <div className="flex items-center gap-1">
                    <ChatRoundedIc />
                    <Typography variant="body3">{messagesCount}</Typography>
                  </div>
                  {/* Row 2: money */}
                  <div className="flex items-center gap-1">
                    <MoneyIc />
                    <Typography variant="body3">{totalRevenue.toFixed(0)}</Typography>
                  </div>
                </div>
              </div>
            </ResponsiveTooltip>

            {/* ARPU */}
            <div className="flex items-center gap-1">
              <span className="text-xs text-auth-muted">ARPU:</span>
              <span className="text-sm font-semibold text-auth-ink">${arpu.toFixed(3)}</span>
            </div>

            {/* ARPU($) */}
            <div className="flex items-center gap-1">
              <span className="text-xs text-auth-muted">ARPU($):</span>
              <span className="text-sm font-semibold text-auth-ink">${arpuUsd.toFixed(3)}</span>
            </div>
          </div>
        );

      case 'status':
        const statusValue = campaign.status || 'in_progress';
        const statusConfig = getStatusConfig(statusValue);
        const isActiveStatus = statusValue === 'in_progress';

        if (isActiveStatus) {
          return (
            <ResponsiveTooltip
              content={tt(
                'accountPage.campaigns.stopCampaignHint',
                "Щоб зупинити кампанію, зв'яжіться з менеджером"
              )}
              placement="top"
            >
              <div className="flex items-center gap-[0.3125rem]">
                <Chip
                  className="px-2 py-1 gap-1 rounded-2"
                  style={{ backgroundColor: statusConfig.bgColor }}
                  size="sm"
                  startContent={
                    <div className="size-[0.4375rem]">
                      <div
                        className="size-[0.4375rem] rounded-full"
                        style={{ backgroundColor: statusConfig.color }}
                      />
                    </div>
                  }
                >
                  <p className="text-xs leading-4 font-regular text-auth-ink">
                    {formatStatus(statusValue)}
                  </p>
                </Chip>
                <InfoCircle />
              </div>
            </ResponsiveTooltip>
          );
        }

        return (
          <div className="flex items-center gap-[0.3125rem]">
            <Chip
              className="px-2 py-1 gap-1 rounded-2"
              style={{ backgroundColor: statusConfig.bgColor }}
              size="sm"
              startContent={
                <div className="size-[0.4375rem]">
                  <div
                    className="size-[0.4375rem] rounded-full"
                    style={{ backgroundColor: statusConfig.color }}
                  />
                </div>
              }
            >
              <p className="text-xs leading-4 font-regular text-auth-ink">
                {formatStatus(statusValue)}
              </p>
            </Chip>
          </div>
        );

      case 'added':
        return (
          <div className="flex items-center gap-1">
            <IcDate />
            <Typography variant="body3" className="text-auth-muted">
              {formatRelativeDate(campaign.created_at)}
            </Typography>
          </div>
        );

      default:
        return null;
    }
  };

  // Пагинация для таблицы
  const pagination = {
    count: totalCount,
    page_size: pageSize,
    page: currentPage,
  };

  return (
    <div className="flex flex-col flex-1 h-full gap-6">
      {/* Заголовок */}
      <Typography variant="h6">{tt('accountPage.campaigns.title', 'Кампании аккаунта')}</Typography>

      {/* Фильтры */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-4 max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
          {/* Поиск по Имя / ID */}
          <Input
            type="text"
            placeholder={tt('accountPage.campaigns.search.placeholder', 'Имя / ID')}
            size="sm"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            endContent={<img src={searchIconUrl} alt="Search" className="w-5 h-5" />}
            className="w-[17.0625rem] h-9 bg-auth-surface border-none rounded-xl"
            classNames={{
              input: 'text-sm',
              inputWrapper: 'rounded-lg h-9 w-[17.0625rem]',
            }}
          />
          {/* Фильтр по статусу */}
          <FilterSelect
            placeholder={tt('accountPage.campaigns.filters.status', 'Статус')}
            options={[
              {
                key: 'in_progress',
                label: tt('accountPage.campaigns.status.in_progress', 'Активна'),
              },
              {
                key: 'in_moderation',
                label: tt('accountPage.campaigns.status.in_moderation', 'На модерации'),
              },
              { key: 'waiting', label: tt('accountPage.campaigns.status.waiting', 'Ожидает') },
              { key: 'canceled', label: tt('accountPage.campaigns.status.canceled', 'Отменена') },
              {
                key: 'completed',
                label: tt('accountPage.campaigns.status.completed', 'Завершена'),
              },
            ]}
            selectedKey={statusFilter}
            onSelectionChange={handleStatusFilterChange}
            variant="campaign"
            autoWidth={true}
            startContent={<Icon icon="solar:bolt-linear" width={20} className="text-neutral-8" />}
          />

          {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}

          {/* Reset filters button */}
          {hasActiveFilters && (
            <Tooltip content="Сбросить фильтры" placement="top">
              <Button
                //@ts-ignore
                size="xs"
                className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
                onPress={handleResetFilters}
              >
                <ResetFiltersIc />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Таблица кампаний */}
      <div className="w-full h-full md:pb-[4.125rem]">
        <UniversalTable
          data={campaigns}
          columns={columns}
          renderCell={renderCell}
          onRowAction={handleRowClick}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          currentPage={currentPage}
          currentPageSize={pageSize}
          isLoading={isLoading}
          emptyContent={
            <div className="text-center py-12">
              <div className="flex flex-col items-center gap-4">
                <EmptyContentClientIc />
                <div className="flex flex-col gap-1.5">
                  <div className="text-xl font-semibold leading-7 text-auth-ink">
                    {tt('accountPage.campaigns.empty.title', 'Нет кампаний')}
                  </div>
                  <div className="text-neutral-500 text-base font-medium leading-6">
                    {tt(
                      'accountPage.campaigns.empty.description',
                      'Кампании появятся после создания заказов'
                    )}
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default CampaignsTab;
